import React, { useState, useRef, useEffect } from 'react';

// Area options that match PropertyArea enum
const areaOptions = [
  { value: 'KIAWAH', label: 'Kiawah' },
  { value: 'ISLE_OF_PALMS', label: 'Isle of Palms' },
  { value: 'ISLE_OF_PALMS_WILD_DUNES', label: 'Isle of Palms: Wild Dunes' },
  { value: 'SEABROOK_ISLAND', label: 'Seabrook Island' },
];

const MultiSelectArea = ({ value = [], onChange, error, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    
    onChange(newValue);
  };

  const handleRemoveOption = (optionValue, e) => {
    e.stopPropagation();
    const newValue = value.filter(v => v !== optionValue);
    onChange(newValue);
  };

  const getSelectedLabels = () => {
    return value.map(val => {
      const option = areaOptions.find(opt => opt.value === val);
      return option ? option.label : val;
    });
  };

  const selectedLabels = getSelectedLabels();

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Main input/display area */}
      <div
        className={`min-h-[42px] w-full rounded-lg border px-3 py-2 cursor-pointer focus:outline-none focus:ring-1 ${
          error 
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
            : 'border-gray-200 focus:border-sky-500 focus:ring-sky-500'
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedLabels.length === 0 ? (
          <span className="text-gray-400">-- Select areas --</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {selectedLabels.map((label, index) => {
              const optionValue = value[index];
              return (
                <span
                  key={optionValue}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800"
                >
                  {label}
                  <button
                    type="button"
                    onClick={(e) => handleRemoveOption(optionValue, e)}
                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-sky-200 focus:outline-none"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              );
            })}
          </div>
        )}
        
        {/* Dropdown arrow */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {areaOptions.map((option) => (
            <div
              key={option.value}
              className={`px-3 py-2 cursor-pointer hover:bg-gray-50 flex items-center ${
                value.includes(option.value) ? 'bg-sky-50' : ''
              }`}
              onClick={() => handleToggleOption(option.value)}
            >
              <input
                type="checkbox"
                checked={value.includes(option.value)}
                onChange={() => {}} // Handled by parent onClick
                className="mr-2 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
              />
              <span className={value.includes(option.value) ? 'font-medium text-sky-900' : 'text-gray-900'}>
                {option.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelectArea;